import React, { useCallback, useEffect, useState } from 'react';
import { Users, Ticket } from 'lucide-react';
import { SharedCartProvider, useSharedCart } from '@/components/shared-cart/SharedCartProvider';
import { SharedCartInterface } from '@/components/shared-cart/SharedCartInterface';
import { usePartyRTC } from '@/party/usePartyRTC';
import { PartyBar } from '@/party/PartyBar';
import { ScreenShareView } from '@/party/ScreenShareView';
import { CheckoutModal } from '@/party/CheckoutModal';
import { useUserStore } from '@/stores/userStore';
import { API_BASE_URL, authHeaders } from '@/services/config';

// The lobby: start a party or punch in a friend's code, the way you'd join
// a game session.
function PartyLobby() {
  const { createSharedCart, joinByInviteCode, isLoading, error } = useSharedCart();
  const { user } = useUserStore();
  const [sessionName, setSessionName] = useState('');
  const [code, setCode] = useState('');
  const [launchError, setLaunchError] = useState<string | null>(null);

  const handleCreate = async (e: React.FormEvent) => {
    e.preventDefault();
    setLaunchError(null);
    try {
      const res = await fetch(`${API_BASE_URL}/vendors?limit=1`, { headers: authHeaders() });
      if (!res.ok) throw new Error(`Failed to load vendors (HTTP ${res.status})`);
      const data = await res.json();
      const vendorId: string | undefined = data.vendors?.[0]?.id;
      if (!vendorId) {
        setLaunchError('No store is connected yet, so a session cannot be started.');
        return;
      }
      await createSharedCart(vendorId, sessionName.trim() || `${user?.displayName || 'My'} party`);
    } catch (err) {
      setLaunchError(err instanceof Error ? err.message : 'Failed to start the party');
    }
  };

  const handleJoin = async (e: React.FormEvent) => {
    e.preventDefault();
    setLaunchError(null);
    if (!code.trim()) return;
    try {
      await joinByInviteCode(code.trim().toUpperCase(), user?.displayName || 'Guest');
    } catch (err) {
      setLaunchError(err instanceof Error ? err.message : 'That code did not work');
    }
  };

  return (
    <div className="mx-auto grid max-w-3xl gap-6 md:grid-cols-2">
      <div className="rounded-2xl bg-den p-6 text-white">
        <div className="mb-4 flex h-10 w-10 items-center justify-center rounded-xl bg-coral/20">
          <Users className="h-5 w-5 text-coral" />
        </div>
        <h2 className="mb-1 text-xl font-bold">Start a party</h2>
        <p className="mb-5 text-sm text-white/60">
          Open a session, get a party code, and shop the same cart together.
        </p>
        <form onSubmit={handleCreate} className="space-y-3">
          <input
            type="text"
            value={sessionName}
            onChange={e => setSessionName(e.target.value)}
            placeholder="Name the session (e.g. Ben's birthday haul)"
            className="w-full rounded-lg border border-denline bg-ink px-3 py-2 text-white placeholder:text-white/30 focus:border-coral focus:outline-none"
          />
          <button type="submit" disabled={isLoading} className="btn-primary w-full">
            {isLoading ? 'Starting…' : 'Start session'}
          </button>
        </form>
      </div>

      <div className="rounded-2xl bg-den p-6 text-white">
        <div className="mb-4 flex h-10 w-10 items-center justify-center rounded-xl bg-radio/20">
          <Ticket className="h-5 w-5 text-radio" />
        </div>
        <h2 className="mb-1 text-xl font-bold">Join with a code</h2>
        <p className="mb-5 text-sm text-white/60">
          Got a party code from a friend? Punch it in and jump into their session.
        </p>
        <form onSubmit={handleJoin} className="space-y-3">
          <input
            type="text"
            value={code}
            onChange={e => setCode(e.target.value.toUpperCase())}
            placeholder="PARTY CODE"
            maxLength={12}
            className="w-full rounded-lg border border-denline bg-ink px-3 py-2 text-center font-mono text-lg font-semibold uppercase tracking-[0.35em] text-coral placeholder:tracking-normal placeholder:text-white/30 focus:border-coral focus:outline-none"
          />
          <button type="submit" disabled={isLoading || !code.trim()} className="btn-primary w-full">
            {isLoading ? 'Joining…' : 'Join session'}
          </button>
        </form>
      </div>

      {(launchError || error) && (
        <p className="md:col-span-2 rounded-lg bg-red-500/10 p-3 text-center text-sm text-red-400">
          {launchError || error}
        </p>
      )}
    </div>
  );
}

// The room: party bar (roster/voice/screen/checkout) over the shared cart.
function PartyRoom() {
  const { sharedCart } = useSharedCart();
  const rtc = usePartyRTC();
  const [inviteCode, setInviteCode] = useState<string | null>(null);
  const [checkoutOpen, setCheckoutOpen] = useState(false);

  const cartId = sharedCart?.id;

  // Fetch (or mint) the session's party code
  const loadInvite = useCallback(async () => {
    if (!cartId) return;
    try {
      const res = await fetch(`${API_BASE_URL}/shared-carts/${cartId}/invites`, { headers: authHeaders() });
      if (res.ok) {
        const body = await res.json();
        const pending = (body.data || []).find(
          (inv: any) => inv.status === 'pending' || inv.status === 'accepted'
        );
        if (pending?.inviteCode) {
          setInviteCode(pending.inviteCode);
          return;
        }
      }
      // No usable invite yet — create one (host and members alike may invite)
      const createRes = await fetch(`${API_BASE_URL}/shared-carts/${cartId}/invites`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...authHeaders() },
        body: JSON.stringify({ expiresIn: 24 }),
      });
      if (createRes.ok) {
        const body = await createRes.json();
        setInviteCode(body.data?.inviteCode || null);
      }
    } catch (err) {
      console.error('Failed to load party code:', err);
    }
  }, [cartId]);

  useEffect(() => {
    loadInvite();
  }, [loadInvite]);

  return (
    <div className="space-y-4">
      <PartyBar rtc={rtc} inviteCode={inviteCode} onCheckout={() => setCheckoutOpen(true)} />

      {rtc.presenterStream && rtc.presenterId && (
        <ScreenShareView stream={rtc.presenterStream} presenterId={rtc.presenterId} />
      )}

      <div className="rounded-2xl bg-white p-4 shadow-card">
        <SharedCartInterface />
      </div>

      {checkoutOpen && <CheckoutModal onClose={() => setCheckoutOpen(false)} />}
    </div>
  );
}

function PartyWorkspace() {
  const { sharedCart } = useSharedCart();
  return sharedCart ? <PartyRoom /> : <PartyLobby />;
}

export function SharedCartPage() {
  return (
    <SharedCartProvider>
      <PartyWorkspace />
    </SharedCartProvider>
  );
}
